import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useStore } from 'core/store';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { ActionButton } from 'core/components/action-button';
import deleteIcon from 'core/assets/svg/delete-icon.svg';
import addIcon from 'core/assets/svg/ic-add.svg';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { getNamedInsuredOptions } from 'common/helpers/farm-helpers';
import useStyles from '../liability-coverages-styles';

const businessPursuitOptionsByState = lookupsJson.farm.businessPursuitOptionsByState;

const FIELD_NAME = 'farmLiabilityCoverage.businessPursuits';

const getPursuitLabel = (type, options) => options.find((o) => o.id === type)?.value || type;

const validationSchema = Yup.object().shape({
  type: Yup.number().required('Type is required'),
  amount: Yup.number().required('Estimated receipts is required'),
  namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
});

const IncidentalBusinessPursuits = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const pursuits = getIn(values, FIELD_NAME) || [];

  const { inPolicyRoute } = useRouteHelper();
  const {
    account: {
      policies: {
        policy: { policy: policyData }
      }
    }
  } = useStore();
  const state = inPolicyRoute
    ? policyData?.state
    : getIn(values, 'correctedAddress.state') || getIn(values, 'farmLocations.0.address.state');
  const pursuitOptions = businessPursuitOptionsByState[state] || [];

  const namedInsuredOptions = getNamedInsuredOptions(values);

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.businessPursuits">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Incidental business pursuits</div>}
        contentRight={pursuits.length > 0 ? <AppliedTag /> : null}
        initialOpen={pursuits.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={pursuits.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {pursuits.map((pursuit, index) => {
            const insureds = pursuit.namedInsureds || [];

            return (
              <CollapsibleListItem
                key={`pursuit-${index}`}
                title={getPursuitLabel(pursuit.type, pursuitOptions)}
                rightSideValues={[]}
                onDelete={() => {
                  const newPursuits = pursuits.filter((_, i) => i !== index);
                  setFieldValue(FIELD_NAME, newPursuits);
                  setFieldTouched(FIELD_NAME);
                  onChangeActive();
                }}
                disabled={disabled}
              >
                <Field
                  xs={6}
                  type="numeric"
                  id={`${FIELD_NAME}[${index}].amount`}
                  name={`${FIELD_NAME}[${index}].amount`}
                  mode="dark"
                  label="Estimated receipts (per year)"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disabled}
                  onChange={onChangeActive}
                />
                <Field
                  xs={6}
                  type="value"
                  id={`${FIELD_NAME}[${index}].productsAndCompletedWork`}
                  name={`${FIELD_NAME}[${index}].productsAndCompletedWork`}
                  mode="dark"
                  label="Coverage for products and completed work"
                  formatter={() => 'Excluded'}
                />
                {insureds.map((_, nestedIndex) => (
                  <Grid container item xs={12} key={`pursuit-${index}-insured-${nestedIndex}`} alignItems="center">
                    <Field
                      xs={nestedIndex > 0 ? 11 : 12}
                      type="select"
                      id={`${FIELD_NAME}[${index}].namedInsureds.${nestedIndex}`}
                      name={`${FIELD_NAME}[${index}].namedInsureds.${nestedIndex}`}
                      mode="dark"
                      label={`Named insured ${String(nestedIndex + 1).padStart(2, '0')}`}
                      options={namedInsuredOptions}
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    {nestedIndex > 0 && (
                      <Grid item xs={1}>
                        <ActionButton
                          onClick={() => {
                            const newInsureds = insureds.filter((_, i) => i !== nestedIndex);
                            setFieldValue(`${FIELD_NAME}[${index}].namedInsureds`, newInsureds);
                            onChangeActive();
                          }}
                          className={classes.deleteInsuredButton}
                          icon={deleteIcon}
                          disabled={disabled}
                          size="small"
                        />
                      </Grid>
                    )}
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button
                    className={classes.addAnotherButton}
                    onClick={() => {
                      setFieldValue(`${FIELD_NAME}[${index}].namedInsureds`, [...insureds, '']);
                      onChangeActive();
                    }}
                    disabled={disabled}
                    startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
                  >
                    Add another named insured
                  </Button>
                </Grid>
              </CollapsibleListItem>
            );
          })}

          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                type: '',
                amount: '',
                namedInsureds: ['']
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const newPursuit = {
                  id: uuidv4(),
                  type: val.type,
                  amount: val.amount,
                  namedInsureds: val.namedInsureds.filter(Boolean)
                };
                setFieldValue(FIELD_NAME, [...pursuits, newPursuit]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm, values: formValues, setFieldValue: setInnerFieldValue }) => (
                <Grid container spacing={2}>
                  <Grid item xs={12} container alignItems="center" wrap="nowrap">
                    <div className={classes.selectFieldWrapper}>
                      <Field
                        xs={12}
                        type="select"
                        id="type"
                        name="type"
                        mode="dark"
                        label="Type of business pursuit"
                        options={pursuitOptions}
                        disabled={disabled}
                      />
                    </div>
                    {formValues.type && (
                      <ActionButton
                        onClick={() => setInnerFieldValue('type', '')}
                        className={classes.deleteInsuredButton}
                        icon={deleteIcon}
                        disabled={disabled}
                        size="small"
                      />
                    )}
                  </Grid>
                  {formValues.type && (
                    <>
                      <Field
                        xs={6}
                        type="numeric"
                        id="amount"
                        name="amount"
                        mode="dark"
                        label="Estimated receipts (per year)"
                        format={{ thousandSeparator: true, prefix: '$' }}
                        disabled={disabled}
                      />
                      <Field
                        xs={6}
                        type="value"
                        id="productsAndCompletedWork"
                        name="productsAndCompletedWork"
                        mode="dark"
                        label="Coverage for products and completed work"
                        formatter={() => 'Excluded'}
                      />
                      {formValues.namedInsureds.map((_, index) => (
                        <Grid item xs={12} key={`insured-${index}`} container alignItems="center" wrap="nowrap">
                          <div className={classes.insuredFieldWrapper}>
                            <Field
                              xs={12}
                              type="select"
                              id={`namedInsureds.${index}`}
                              name={`namedInsureds.${index}`}
                              mode="dark"
                              label={`Named insured ${String(index + 1).padStart(2, '0')}`}
                              options={namedInsuredOptions}
                              disabled={disabled}
                            />
                          </div>
                          {index > 0 && (
                            <ActionButton
                              onClick={() => {
                                const newInsureds = formValues.namedInsureds.filter((_, i) => i !== index);
                                setInnerFieldValue('namedInsureds', newInsureds);
                              }}
                              className={classes.deleteInsuredButton}
                              icon={deleteIcon}
                              disabled={disabled}
                              size="small"
                            />
                          )}
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          className={classes.addAnotherButton}
                          onClick={() => setInnerFieldValue('namedInsureds', [...formValues.namedInsureds, ''])}
                          disabled={disabled}
                          startIcon={<img src={addIcon} alt="add" width={28} height={28} />}
                        >
                          Add another named insured
                        </Button>
                      </Grid>
                      <Grid container alignItems="center" className={classes.addButtonRow}>
                        <Button
                          type="button"
                          mode="big"
                          variant="contained"
                          color="secondary"
                          className={classes.addButton}
                          disabled={disabled}
                          onClick={submitForm}
                        >
                          Add Item
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

IncidentalBusinessPursuits.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default IncidentalBusinessPursuits;
